import type { NormalizedEventType } from './billing.js';
import { WorkflowStep } from './workflow-steps.js';

/**
 * The shape of every Step Functions state machine, as data.
 *
 * Before this file, the routing lived only in `infra/lib/constructs/workflows.ts` — which
 * meant it existed only inside AWS. Locally, `SfnEventBus` warns and skips when `SFN_ARN_*`
 * is absent, so creating a property, submitting an inspection or receiving a payment wrote a
 * row and then nothing happened: task creation, review tasks, notifications and dunning were
 * all invisible outside a deployed stage. Every workflow test had to call step handlers
 * directly and assert the sequence by hand.
 *
 * Three consumers read these tables now, which is what makes them trustworthy:
 *   1. `infra/lib/constructs/workflows.ts` folds them into the real state machines.
 *   2. `apps/api/src/lib/events/local-sfn-runner.ts` executes them in-process for
 *      `EVENT_BUS_DRIVER=local` (dev and the T3 `stack` test tier).
 *   3. `apps/api/src/__tests__/lib/workflow-routes-sync.test.ts` synthesises the CDK stack
 *      and asserts the emitted ASL still matches, so the two can never drift apart.
 *
 * The dunning machine is deliberately absent: its shape is already derived from
 * `DUNNING_STAGES` in dunning.ts, which is the same single-source-of-truth trick applied to
 * a schedule rather than a chain.
 */

/**
 * One state in a chain. `id` is the CDK construct id and the ASL state name — it has to be
 * unique within a machine, which is why it is carried separately from `step`: `MarkProcessed`
 * and `RecordPayment` each appear in several payment branches under different state names.
 */
export interface WorkflowChainStep {
  id: string;
  step: WorkflowStep;
}

/** A node is either a single state or a Parallel whose branches run concurrently. */
export type WorkflowNode =
  | ({ kind: 'step' } & WorkflowChainStep)
  | { kind: 'parallel'; id: string; branches: WorkflowChainStep[][] };

const step = (id: string, s: WorkflowStep): WorkflowNode => ({ kind: 'step', id, step: s });

/**
 * Machines whose shape is a fixed chain. Keyed by CDK construct id.
 *
 * `PropertyCreated` no longer creates tasks — only a confirmed payment
 * does, via `PaymentWebhookReceived`. `InspectionApproved` likewise: approving an inspection
 * is not a payment, so it grants no monitoring entitlement (issue #430).
 */
export const WORKFLOW_CHAINS: Record<string, readonly WorkflowNode[]> = {
  PropertyCreated: [step('NotifyPropertyCreated', WorkflowStep.NotifyPropertyCreated)],
  InspectionSubmitted: [step('GenerateReviewTask', WorkflowStep.GenerateReviewTask)],
  InspectionApproved: [
    step('CompleteReviewTask', WorkflowStep.CompleteReviewTask),
    {
      kind: 'parallel',
      id: 'InspectionApprovedParallel',
      branches: [[{ id: 'UpdateSubscription', step: WorkflowStep.UpdateSubscription }]],
    },
    step('NotifyOwner', WorkflowStep.NotifyOwner),
  ],
  InspectionRejected: [
    step('CloseReviewTask', WorkflowStep.CloseReviewTask),
    {
      kind: 'parallel',
      id: 'InspectionRejectedParallel',
      branches: [
        [{ id: 'CreateRetryTask', step: WorkflowStep.CreateRetryTask }],
        [{ id: 'ArchiveRejectedMedia', step: WorkflowStep.ArchiveRejectedMedia }],
      ],
    },
  ],
  PropertyMonitoringStopped: [
    step('NotifyMonitoringStopped', WorkflowStep.NotifyMonitoringStopped),
  ],
  PropertyArchived: [
    {
      kind: 'parallel',
      id: 'PropertyArchivedParallel',
      branches: [
        [{ id: 'DeletePropertyMedia', step: WorkflowStep.DeletePropertyMedia }],
        [{ id: 'NotifyPropertyArchived', step: WorkflowStep.NotifyPropertyArchived }],
      ],
    },
  ],
};

export interface PaymentWebhookRoute {
  when: NormalizedEventType;
  chain: readonly WorkflowChainStep[];
}

/**
 * The `PaymentWebhookReceived` Choice, in ASL evaluation order.
 *
 * Every branch ends in `MarkProcessed` except the default, which is terminal at
 * `MarkIgnored` — an ignored event was never claimed for processing, so there is nothing to
 * mark done. `ResolveContext` runs before the Choice and is not part of any branch.
 *
 * Note `payment.refunded` and `subscription.resumed`: both adapters normalise to them, but
 * no branch handles either, so they fall to the default and land as `IGNORED`. That is the
 * behaviour today, faithfully represented — not an omission in this table.
 */
export const PAYMENT_WEBHOOK_ROUTES: readonly PaymentWebhookRoute[] = [
  {
    when: 'payment.succeeded',
    chain: [
      { id: 'RecordPaymentSucceeded', step: WorkflowStep.RecordPayment },
      { id: 'ActivateOrRenew', step: WorkflowStep.ActivateOrRenew },
      { id: 'CreateMonitoringTaskPayment', step: WorkflowStep.CreateMonitoringTaskPayment },
      { id: 'NotifyOwnerPayment', step: WorkflowStep.NotifyOwnerPayment },
      { id: 'MarkProcessedSucceeded', step: WorkflowStep.MarkProcessed },
    ],
  },
  {
    when: 'payment.failed',
    chain: [
      { id: 'RecordPaymentFailed', step: WorkflowStep.RecordPayment },
      { id: 'MarkPastDue', step: WorkflowStep.MarkPastDue },
      { id: 'NotifyPaymentFailed', step: WorkflowStep.NotifyPaymentFailed },
      { id: 'MarkProcessedFailed', step: WorkflowStep.MarkProcessed },
    ],
  },
  {
    when: 'subscription.authenticated',
    chain: [
      { id: 'MarkAuthenticated', step: WorkflowStep.MarkAuthenticated },
      { id: 'MarkProcessedAuthenticated', step: WorkflowStep.MarkProcessed },
    ],
  },
  {
    when: 'subscription.activated',
    chain: [
      { id: 'SetGatewayId', step: WorkflowStep.SubscriptionActivated },
      { id: 'MarkProcessedActivated', step: WorkflowStep.MarkProcessed },
    ],
  },
  {
    when: 'subscription.cancelled',
    chain: [
      { id: 'CancelSubscription', step: WorkflowStep.CancelSubscription },
      { id: 'NotifyCancelled', step: WorkflowStep.NotifyCancelled },
      { id: 'MarkProcessedCancelled', step: WorkflowStep.MarkProcessed },
    ],
  },
  {
    when: 'subscription.paused',
    chain: [
      { id: 'MarkPaused', step: WorkflowStep.MarkPaused },
      { id: 'MarkProcessedPaused', step: WorkflowStep.MarkProcessed },
    ],
  },
];

/** The Choice's `otherwise` branch. Terminal — no MarkProcessed, see above. */
export const PAYMENT_WEBHOOK_DEFAULT_ROUTE: readonly WorkflowChainStep[] = [
  { id: 'MarkIgnored', step: WorkflowStep.MarkIgnoredPayment },
];

/** Runs before the Choice, outside every branch. */
export const PAYMENT_WEBHOOK_ENTRY_STEP: WorkflowChainStep = {
  id: 'ResolveContext',
  step: WorkflowStep.ResolveContext,
};

/** The Parallel's catch target — every failure inside the try branch lands here. */
export const PAYMENT_WEBHOOK_CATCH_STEP: WorkflowChainStep = {
  id: 'MarkFailed',
  step: WorkflowStep.MarkFailed,
};

/** Resolve the branch an event takes, mirroring the ASL Choice's ordered evaluation. */
export function routeForNormalizedType(normalizedType: string): readonly WorkflowChainStep[] {
  return (
    PAYMENT_WEBHOOK_ROUTES.find((r) => r.when === normalizedType)?.chain ??
    PAYMENT_WEBHOOK_DEFAULT_ROUTE
  );
}
